import { useEffect, useState } from 'react';
import { Check, Lock, MinusCircle, Undo2, ChevronRight, Printer, AlertTriangle } from 'lucide-react';
import { api } from '@/lib/api';

const label = (stage, key) => stage.fields.find((f) => f.key === key)?.label || key;
const show = (v) => (v === true ? 'Yes' : v === false ? 'No' : String(v));
const visible = (f, data) => !f.show_if || f.show_if.in.includes(String(data[f.show_if.field] || ''));

export default function CaseWorkflow({ caseId, onChanged }) {
  const [wf, setWf] = useState(null);
  const [form, setForm] = useState({});
  const [note, setNote] = useState('');
  const [naMode, setNaMode] = useState(false);
  const [naReason, setNaReason] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const load = () => api(`/cases/${caseId}/workflow`).then((d) => {
    setWf(d);
    const current = d.stages.find((s) => s.state === 'current');
    const defaults = {};
    (current?.fields || []).forEach((f) => { if (f.prefill === 'me') defaults[f.key] = d.me; });
    setForm(defaults);
    setNote('');
    setNaMode(false);
    setNaReason('');
  });
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [caseId]);

  const submit = async (stageKey, action) => {
    setError('');
    setBusy(true);
    try {
      await api(`/cases/${caseId}/workflow/${stageKey}`, {
        method: 'POST',
        body: JSON.stringify({ action, data: form, note, na_reason: naReason }),
      });
      await load();
      onChanged?.();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const undo = async (stageKey) => {
    setError('');
    setBusy(true);
    try {
      await api(`/cases/${caseId}/workflow/${stageKey}`, { method: 'DELETE' });
      await load();
      onChanged?.();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const printForm = async (formKey) => {
    setError('');
    const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/cases/${caseId}/forms/${formKey}.pdf`, { credentials: 'include' });
    if (!res.ok) { setError('Could not generate that form.'); return; }
    const url = URL.createObjectURL(await res.blob());
    const a = document.createElement('a');
    a.href = url;
    a.download = `FairWay-${caseId}-${formKey}.pdf`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  if (!wf) return <p className="muted">Loading workflow…</p>;

  const lastRecorded = [...wf.stages].reverse().find((s) => s.record)?.key;

  return (
    <div className="stack" data-testid="case-workflow">
      <div className="wf-progress">
        <div className="wf-progress-bar"><span style={{ width: `${(wf.completed_count / wf.total) * 100}%` }} /></div>
        <small data-testid="workflow-progress">Stage {Math.min(wf.completed_count + 1, wf.total)} of {wf.total} · {wf.completed_count} recorded</small>
      </div>
      {error && <div className="error" data-testid="workflow-error">{error}</div>}
      {wf.warning_summary?.mandatory_hearing_required && (
        <div className="cba-alert" data-testid="mandatory-hearing-alert">
          <AlertTriangle size={15} />
          <span>
            <b>{wf.warning_summary.active_count} active written warnings in the last 12 months</b> — a mandatory hearing is
            required for this seafarer under CBA Art. 31.
          </span>
        </div>
      )}

      {wf.stages.map((s) => {
        const rec = s.record;
        const isCurrent = s.state === 'current';
        const na = s.state === 'not_applicable';
        return (
          <div className={`wf-stage ${s.state}`} key={s.key} data-testid={`stage-${s.key}`}>
            <div className="wf-stage-head">
              <span className="wf-badge">
                {rec ? (na ? <MinusCircle size={13} /> : <Check size={13} />) : isCurrent ? <ChevronRight size={13} /> : <Lock size={12} />}
                {s.order}
              </span>
              <div>
                <strong>{s.title}</strong>
                {s.article && <span className="cba-badge" data-testid={`stage-article-${s.key}`}>{s.article}</span>}
                <p className="muted">{s.description}</p>
                {s.deadline && (
                  <p className={`wf-deadline ${s.deadline.overdue ? 'overdue' : ''} ${s.deadline.compliance || ''}`} data-testid={`stage-deadline-${s.key}`}>
                    {s.deadline.due
                      ? <>Due {s.deadline.due}
                        {s.deadline.compliance === 'met' && ' · met'}
                        {s.deadline.compliance === 'breached' && ' · CBA time limit exceeded'}
                        {s.deadline.days_remaining !== undefined && (s.deadline.overdue
                          ? ` · ${Math.abs(s.deadline.days_remaining)} days overdue`
                          : ` · ${s.deadline.days_remaining} days remaining`)}
                        {' · '}{s.deadline.basis}</>
                      : <>{s.deadline.days}-day CBA limit · {s.deadline.basis}</>}
                  </p>
                )}
              </div>
              <span className="wf-state" data-testid={`stage-state-${s.key}`}>
                {rec ? (na ? 'Not applicable' : 'Completed') : isCurrent ? 'In progress' : 'Locked'}
              </span>
            </div>

            {rec && (
              <div className="wf-record">
                {na && <p className="timeline-text"><b>Reason:</b> {rec.na_reason}</p>}
                {!na && s.fields.filter((f) => rec.data?.[f.key] !== undefined && rec.data?.[f.key] !== '' && rec.data?.[f.key] !== null).map((f) => (
                  <p className="timeline-text" key={f.key}><b>{label(s, f.key)}:</b> {show(rec.data[f.key])}</p>
                ))}
                {rec.note && <p className="timeline-text"><b>Note:</b> {rec.note}</p>}
                <small className="muted">{rec.completed_by_name} · {(rec.completed_at || '').slice(0, 16).replace('T', ' ')}</small>
                {lastRecorded === s.key && (
                  <button className="ghost-btn tiny" data-testid={`undo-stage-${s.key}`} onClick={() => undo(s.key)} disabled={busy}>
                    <Undo2 size={12} /> Undo this stage
                  </button>
                )}
              </div>
            )}

            {s.forms && s.state !== 'locked' && (
              <div className="wf-forms" data-testid={`stage-forms-${s.key}`}>
                <small className="muted">
                  {s.state === 'current'
                    ? 'Printable forms — the details you record in this stage are filled in once the stage is completed; print, sign and upload the scan in Attachments'
                    : 'Printable forms — filled in with the details recorded at this stage; print, sign and upload the scan in Attachments'}
                </small>
                <div className="wf-actions">
                  {s.forms.map((f) => (
                    <button
                      type="button"
                      className="ghost-btn tiny"
                      key={f.key}
                      data-testid={`print-form-${f.key}`}
                      onClick={() => printForm(f.key)}
                    >
                      <Printer size={12} /> {f.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {isCurrent && (
              <div className="wf-form stack">
                {s.key === 'outcome' && wf.crew_service && (
                  <div className="wf-hint" data-testid="service-hint">
                    Crew record: hired {wf.crew_service.hire_date || '—'} · {wf.crew_service.completed_contracts} completed
                    contract{wf.crew_service.completed_contracts === 1 ? '' : 's'}
                    {wf.crew_service.years_since_hire ? ` · ${wf.crew_service.years_since_hire} years since first hire` : ''}.
                    Enter aggregated contracted service for the §5-6 notice period (1 month, 2 months from 5 years,
                    3 months from 10 years, 3 months for the Master).
                  </div>
                )}
                {s.key === 'outcome' && wf.consistency?.outcomes?.length > 0 && (
                  <div className="wf-consistency" data-testid="consistency-check">
                    <small className="muted">
                      Consistency check — previous outcomes for “{wf.consistency.category}” across the fleet
                      {Object.entries(wf.consistency.counts).map(([k, v]) => ` · ${k}: ${v}`)}
                    </small>
                    {wf.consistency.outcomes.map((o) => (
                      <small key={o.case_id} className="muted">
                        {o.case_id} · {o.vessel} · {o.severity} → <b>{o.sanction}</b>{o.decision_date ? ` (${o.decision_date})` : ''}
                      </small>
                    ))}
                  </div>
                )}
                {!naMode && s.fields.filter((f) => visible(f, form)).map((f) => (
                  <label className="field" key={f.key}>
                    <span>{f.label}{f.required || f.require_true ? ' *' : ''}</span>
                    {f.type === 'textarea' && (
                      <textarea data-testid={`field-${s.key}-${f.key}`} value={form[f.key] || ''} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} />
                    )}
                    {f.type === 'select' && (
                      <select data-testid={`field-${s.key}-${f.key}`} value={form[f.key] || ''} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}>
                        <option value="">— Select —</option>
                        {f.options.map((o) => <option key={o} value={o}>{o}</option>)}
                      </select>
                    )}
                    {f.type === 'checkbox' && (
                      <input type="checkbox" data-testid={`field-${s.key}-${f.key}`} checked={!!form[f.key]} onChange={(e) => setForm({ ...form, [f.key]: e.target.checked })} />
                    )}
                    {['text', 'date', 'number'].includes(f.type) && (
                      <input type={f.type} data-testid={`field-${s.key}-${f.key}`} value={form[f.key] || ''} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} />
                    )}
                  </label>
                ))}
                {!naMode && (
                  <label className="field"><span>Stage note (optional)</span>
                    <textarea data-testid={`stage-note-${s.key}`} value={note} onChange={(e) => setNote(e.target.value)} />
                  </label>
                )}
                {naMode && (
                  <label className="field"><span>Why is this stage not applicable? *</span>
                    <textarea data-testid={`na-reason-${s.key}`} value={naReason} onChange={(e) => setNaReason(e.target.value)} />
                  </label>
                )}
                <div className="wf-actions">
                  {!naMode ? (
                    <>
                      <button className="primary-btn" data-testid={`complete-stage-${s.key}`} onClick={() => submit(s.key, 'complete')} disabled={busy}>
                        <Check size={15} /> Complete stage
                      </button>
                      <button className="ghost-btn" data-testid={`na-stage-${s.key}`} onClick={() => setNaMode(true)} disabled={busy}>
                        <MinusCircle size={13} /> Mark not applicable
                      </button>
                    </>
                  ) : (
                    <>
                      <button className="primary-btn" data-testid={`confirm-na-${s.key}`} onClick={() => submit(s.key, 'not_applicable')} disabled={busy}>
                        Confirm not applicable
                      </button>
                      <button className="ghost-btn" data-testid={`cancel-na-${s.key}`} onClick={() => { setNaMode(false); setNaReason(''); }} disabled={busy}>
                        Cancel
                      </button>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        );
      })}
      {!wf.current_stage && <div className="empty-inline" data-testid="workflow-complete">All {wf.total} workflow stages have been recorded.</div>}
    </div>
  );
}
